/**
 * S3UploadButton Component
 * 
 * Provides a Material-UI button for uploading files and folders to S3.
 * Integrates with S3UploadContext for managing upload jobs with queue management,
 * pause/resume/cancel logic, and concurrency control (max 5 concurrent uploads).
 * 
 * Features:
 * - Two upload modes: individual files or entire folder structure
 * - Disabled based on authentication status and write permissions
 * - Integrates with S3UploadContext for state management
 * - Follows BulkDownloadButton styling and positioning patterns
 * - Mobile-friendly touch targets
 * - Comprehensive accessibility support (ARIA labels)
 * - Dark theme compatible
 * 
 * Usage:
 * ```tsx
 * <S3UploadButton
 *   currentPath="reports/2024"
 *   authenticated={true}
 *   writePermission={true}
 *   mode="files"
 * />
 * ```
 */

import React, { ChangeEvent, useCallback, useMemo, useRef } from 'react';
import {
  Button,
  Tooltip,
  Box,
  ButtonProps,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
} from '@mui/icons-material';
import toast from 'react-hot-toast';
import { useS3Upload } from '../../hooks/useS3Upload';
import { themeColors } from '../../utils/theme';

// ============================================================================
// Type Definitions
// ============================================================================

/**
 * Upload mode for the button
 * - 'files': Allow multiple individual files selection
 * - 'folder': Allow entire folder selection (webkitdirectory)
 */
export type UploadMode = 'files' | 'folder';

/**
 * Props for the S3UploadButton component
 */
export interface S3UploadButtonProps {
  /** Current S3 path where files will be uploaded */
  currentPath: string;
  
  /** Whether user is authenticated */
  authenticated: boolean;
  
  /** Whether user has write permission to the current path */
  writePermission: boolean;
  
  /** Upload mode: 'files' for individual files or 'folder' for directory upload */
  mode?: UploadMode;
  
  /** Optional custom label for the button */
  label?: string;
  
  /** Optional variant override */
  variant?: 'contained' | 'outlined' | 'text';
  
  /** Optional size override */
  size?: 'small' | 'medium' | 'large';
  
  /** Optional callback when upload starts */
  onUploadStart?: (jobId: string, fileCount: number) => void;
  
  /** Optional callback when upload completes */
  onUploadComplete?: (jobId: string, success: boolean) => void;
  
  /** Optional callback when upload fails */
  onUploadError?: (jobId: string, error: string) => void;
  
  /** Additional MUI Button props to override or extend */
  buttonProps?: Partial<ButtonProps>;
  
  /** Optional aria label override */
  ariaLabel?: string;
  
  /** Optional aria description override */
  ariaDescription?: string;
}

// ============================================================================
// Component Implementation
// ============================================================================

/**
 * S3UploadButton Component
 * 
 * Renders a button that initiates file/folder uploads to S3.
 * Uses hidden file input elements to handle browser file selection.
 * Integrates with S3UploadContext to manage upload jobs.
 */
const S3UploadButton: React.FC<S3UploadButtonProps> = ({
  currentPath,
  authenticated,
  writePermission,
  mode = 'files',
  label = 'Upload',
  variant = 'contained',
  size = 'medium',
  onUploadStart,
  onUploadComplete,
  onUploadError,
  buttonProps = {},
  ariaLabel,
  ariaDescription,
}) => {
  // Get upload context for starting jobs with cache invalidation
  const { startUploadWithCacheInvalidation } = useS3Upload();
  
  // Create refs for hidden file input elements
  const fileInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);

  /**
   * Determine if button should be disabled
   */
  const isDisabled = !authenticated || !writePermission;

  /**
   * Generate tooltip text based on state
   */
  const tooltipText = useMemo(() => {
    if (!authenticated) {
      return 'Please log in to upload files';
    }
    if (!writePermission) {
      return 'You do not have permission to upload to this location';
    }
    if (mode === 'folder') {
      return 'Upload an entire folder to S3';
    }
    return 'Upload files to S3';
  }, [authenticated, writePermission, mode]);

  /**
   * Handle file selection from file input
   */
  const handleFileSelect = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files || []);
      
      if (files.length === 0) {
        return;
      }

      try {
        // Log the upload action
        console.log('[S3Upload] Starting file upload', {
          fileCount: files.length,
          currentPath,
          mode,
          fileNames: files.map(f => f.name),
          totalSize: files.reduce((sum, f) => sum + f.size, 0),
        });

        // Start upload job with automatic cache invalidation on completion
        const jobId = await startUploadWithCacheInvalidation(files, currentPath);
        
        // Call optional callback
        onUploadStart?.(jobId, files.length);
        
        // Show success toast
        toast.success(`Uploading ${files.length} file(s)`);
        
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        console.error('[S3Upload] Error starting upload', error);
        toast.error(`Upload failed: ${errorMessage}`);
        onUploadError?.('', errorMessage);
      }
      
      // Reset input so same file can be selected again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [currentPath, mode, startUploadWithCacheInvalidation, onUploadStart, onUploadError]
  );

  /**
   * Handle folder selection from directory input
   */
  const handleFolderSelect = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files || []);
      
      if (files.length === 0) {
        return;
      }

      try {
        // Log the upload action with folder structure info
        console.log('[S3Upload] Starting folder upload', {
          fileCount: files.length,
          currentPath,
          files: files.map(f => ({
            name: f.name,
            webkitRelativePath: (f as any).webkitRelativePath,
          })),
          totalSize: files.reduce((sum, f) => sum + f.size, 0),
        });

        // Start upload job with automatic cache invalidation on completion
        const jobId = await startUploadWithCacheInvalidation(files, currentPath);
        
        // Call optional callback
        onUploadStart?.(jobId, files.length);
        
        // Show success toast
        toast.success(`Uploading folder with ${files.length} file(s)`);
        
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        console.error('[S3Upload] Error starting folder upload', error);
        toast.error(`Upload failed: ${errorMessage}`);
        onUploadError?.('', errorMessage);
      }
      
      // Reset input so same folder can be selected again
      if (folderInputRef.current) {
        folderInputRef.current.value = '';
      }
    },
    [currentPath, startUploadWithCacheInvalidation, onUploadStart, onUploadError]
  );

  /**
   * Trigger file input click
   */
  const handleButtonClick = useCallback(() => {
    if (mode === 'folder') {
      folderInputRef.current?.click();
    } else {
      fileInputRef.current?.click();
    }
  }, [mode]);

  /**
   * Generate accessible label
   */
  const accessibleLabel = useMemo(() => {
    if (ariaLabel) {
      return ariaLabel;
    }
    if (mode === 'folder') {
      return 'Upload folder to S3';
    }
    return 'Upload files to S3';
  }, [ariaLabel, mode]);

  /**
   * Generate accessible description
   */
  const accessibleDescription = useMemo(() => {
    if (ariaDescription) {
      return ariaDescription;
    }
    if (isDisabled) {
      return tooltipText;
    }
    if (mode === 'folder') {
      return 'Click to select a folder to upload to S3';
    }
    return 'Click to select files to upload to S3';
  }, [ariaDescription, isDisabled, tooltipText, mode]);

  return (
    <>
      {/* Hidden file input for individual files */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        onChange={handleFileSelect}
        style={{ display: 'none' }}
        aria-hidden="true"
        data-testid="file-input"
      />
      
      {/* Hidden file input for folder selection */}
      <input
        ref={folderInputRef}
        type="file"
        multiple
        // @ts-ignore - webkitdirectory is not in standard TypeScript DOM types
        webkitdirectory=""
        onChange={handleFolderSelect}
        style={{ display: 'none' }}
        aria-hidden="true"
        data-testid="folder-input"
      />

      {/* Main Button */}
      <Tooltip title={tooltipText} placement="top">
        <span>
          <Button
            variant={variant}
            size={size}
            startIcon={<CloudUploadIcon />}
            onClick={handleButtonClick}
            disabled={isDisabled}
            aria-label={accessibleLabel}
            aria-description={accessibleDescription}
            data-testid="upload-button"
            sx={{
              backgroundColor: variant === 'contained' ? themeColors.primary : undefined,
              color: variant === 'contained' ? 'white' : themeColors.primary,
              '&:hover': {
                backgroundColor: variant === 'contained' ? `${themeColors.primary}dd` : undefined,
              },
              '&:disabled': {
                opacity: 0.6,
              },
            }}
            {...buttonProps}
          >
            {label}
          </Button>
        </span>
      </Tooltip>
    </>
  );
};

export default S3UploadButton;
